<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type { RegionType, RegionPayloadType } from "@/common/types/region";

import { baseInput, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: RegionType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  owner_id: props.data?.region_id,
  name: "",
  slug: "",
  code: "",
  description: "",
};

const payload = ref<RegionPayloadType>({ ...DEFAULT_DATA });

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, slug, code, description } = props.data;

  payload.value = {
    ...payload.value,
    name,
    slug,
    code,
    description,
  };
}

function maskCode(data: string) {
  return data?.replaceAll(" ", "-")?.toUpperCase();
}

function maskSlug(data: string) {
  return data?.replaceAll(" ", "-")?.toLowerCase();
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-region">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('region.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("region.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.code"
      :value="payload.code"
      :mask="maskCode"
      :placeholder="t('region.placeholders.code')"
      @update:modelValue="debouceUpdate('code')"
    >
      <template #label> {{ t("region.labels.code") }}</template>
    </base-input>

    <base-input
      v-model="payload.slug"
      :value="payload.slug"
      :placeholder="t('region.placeholders.slug')"
      :mask="maskSlug"
      @update:modelValue="debouceUpdate('slug')"
    >
      <template #label> {{ t("region.labels.slug") }}</template>
    </base-input>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("region.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-region {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
